import React, { useState, useEffect } from 'react';
import { Rocket, ShieldCheck, Briefcase, GraduationCap, ChevronLeft, ExternalLink, Loader2, CheckCircle2, Trash2, MapPin } from 'lucide-react';

interface SavedJob {
    ID: number;
    job_title: string;
    company: string;
    job_url: string;
    mode: string;
    verdict: string;
    confidence: number;
    CreatedAt: string;
}

interface CareerLaunchpadProps {
    onBack?: () => void;
}

const CareerLaunchpad: React.FC<CareerLaunchpadProps> = ({ onBack }) => {
    const [selectedMode, setSelectedMode] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedJobs, setSavedJobs] = useState<SavedJob[]>([]);
    const [jobsLoading, setJobsLoading] = useState(true);

    useEffect(() => {
        fetchMode();
        fetchSavedJobs();
    }, []);

    const fetchSavedJobs = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/student/jobs', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSavedJobs(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch saved jobs', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const handleDeleteJob = async (id: number) => {
        if (!confirm('Are you sure you want to delete this saved job?')) return;
        
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/student/jobs?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setSavedJobs(prev => prev.filter(job => job.ID !== id));
            }
        } catch (error) {
            console.error('Failed to delete job', error);
        }
    };

    const fetchMode = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/student/extension/mode', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedMode(data.mode);
            }
        } catch (error) {
            console.error('Failed to fetch extension mode', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectMode = async (mode: string) => {
        setSaving(true);
        setSelectedMode(mode);
        try {
            const token = localStorage.getItem('token');
            await fetch('/api/student/extension/mode', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ mode })
            });
        } catch (error) {
            console.error('Failed to save extension mode', error);
        } finally {
            setSaving(false);
        }
    };

    const options = [
        {
            id: 'visa',
            title: 'Visa Sponsorship Checker',
            description: 'AI will scan job descriptions for keywords and employer history to predict if they offer Visa Sponsorship.',
            icon: ShieldCheck,
            color: 'bg-indigo-500 text-white',
            border: 'border-indigo-200 border-2 shadow-indigo-100',
            bg: 'bg-indigo-50/50'
        },
        {
            id: 'internship',
            title: 'Internship Checker',
            description: 'AI detects if a role is secretly an internship, graduate scheme, or placement, filtering out permanent senior roles.',
            icon: GraduationCap,
            color: 'bg-emerald-500 text-white',
            border: 'border-emerald-200 border-2 shadow-emerald-100',
            bg: 'bg-emerald-50/50'
        },
        {
            id: 'fulltime',
            title: 'Full-Time Role Checker',
            description: 'AI specifically looks for permanent, full-time positions suitable for graduates and experienced hires.',
            icon: Briefcase,
            color: 'bg-rose-500 text-white',
            border: 'border-rose-200 border-2 shadow-rose-100',
            bg: 'bg-rose-50/50'
        }
    ];

    const jobBoards = [
        { name: 'LinkedIn', url: 'https://www.linkedin.com/jobs' },
        { name: 'Indeed', url: 'https://www.indeed.co.uk/' },
        { name: 'Reed', url: 'https://www.reed.co.uk/' },
        { name: 'Totaljobs', url: 'https://www.totaljobs.com/' }
    ];

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 animate-pulse text-[#009B8F]">
                <Loader2 className="animate-spin mb-4" size={32} />
                <p className="font-serif">Loading Career Launchpad...</p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn max-w-6xl mx-auto space-y-12">
            {onBack && (
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#009B8F] transition-all mb-4"
                >
                    <ChevronLeft size={16} /> Back to Dashboard
                </button>
            )}

            <div className="bg-[#011E26] p-14 rounded-2xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-8">
                    <Rocket size={40} className="text-[#009B8F]" />
                </div>
                <p className="text-[10px] font-bold text-[#009B8F] uppercase tracking-widest mb-2">Career Services</p>
                <h2 className="text-3xl font-serif font-bold text-white mb-3">Career Launchpad</h2>
                <p className="text-sm text-white/60 max-w-2xl leading-relaxed">
                    Choose your targeted job-hunting mode below to power the browser extension.
                </p>
                {saving && (
                    <div className="mt-6 flex items-center gap-3 text-[#009B8F] bg-white/5 px-6 py-2.5 rounded-xl border border-white/10">
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-xs font-semibold">Saving preference...</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {options.map((option) => {
                    const isSelected = selectedMode === option.id;
                    return (
                        <div
                            key={option.id}
                            onClick={() => handleSelectMode(option.id)}
                            className={`
                                group relative p-8 rounded-2xl transition-all duration-300 cursor-pointer border-2
                                ${isSelected 
                                    ? 'bg-[#009B8F]/5 border-[#009B8F] shadow-md -translate-y-1' 
                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}
                            `}
                        >
                            <div className="mb-6 relative">
                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${option.color}`}>
                                    <option.icon size={24} />
                                </div>
                                {isSelected && (
                                    <div className="absolute top-0 right-0 w-3 h-3 bg-[#009B8F] rounded-full animate-pulse"></div>
                                )}
                            </div>

                            <h3 className={`text-lg font-bold mb-3 ${isSelected ? 'text-[#009B8F]' : 'text-slate-800'}`}>
                                {option.title}
                            </h3>
                            <p className="text-sm leading-relaxed text-slate-500">
                                {option.description}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Saved Jobs UI */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#009B8F]/10 rounded-xl flex items-center justify-center">
                            <Briefcase className="text-[#009B8F]" size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-serif font-bold text-slate-800">Saved Portfolio</h2>
                            <p className="text-xs text-slate-400 mt-0.5">{savedJobs.length} saved jobs</p>
                        </div>
                    </div>
                </div>

                {jobsLoading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="animate-spin text-[#1DE9B6]" size={32} />
                    </div>
                ) : savedJobs.length === 0 ? (
                    <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50">
                        <Briefcase size={36} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-slate-400 text-sm">No saved jobs yet</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {savedJobs.map((job) => (
                            <div 
                                key={job.ID} 
                                className="group rounded-2xl border border-gray-200 hover:border-[#009B8F]/30 hover:shadow-md transition-all p-6 flex flex-col"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex-1">
                                        <h4 className="font-bold text-slate-800 text-base leading-tight mb-1 truncate" title={job.job_title}>{job.job_title}</h4>
                                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                            <MapPin size={12} className="text-[#009B8F]" />
                                            <span>{job.company}</span>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleDeleteJob(job.ID)}
                                        className="opacity-0 group-hover:opacity-100 transition-all p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                                
                                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
                                    <div className="flex flex-wrap gap-2">
                                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                                            job.verdict.toLowerCase().includes('likely') || job.verdict.toLowerCase().includes('internship') || job.verdict.toLowerCase().includes('full-time') 
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                            : job.verdict.toLowerCase().includes('unclear') ? 'bg-amber-50 text-amber-700 border-amber-200'
                                            : 'bg-red-50 text-red-700 border-red-200'
                                        }`}>
                                            {job.verdict}
                                        </span>
                                        <span className="text-xs bg-gray-50 text-slate-500 px-2.5 py-1 rounded-full border border-gray-200">
                                            {job.mode} Mode
                                        </span>
                                    </div>
                                    <a 
                                        href={job.job_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs font-semibold text-[#009B8F] hover:text-[#011E26] transition-colors flex items-center gap-1.5"
                                    >
                                        View <ExternalLink size={12} />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Install Extension Banner */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-8 flex flex-col md:flex-row items-center gap-6">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-serif font-bold text-2xl text-amber-600">N</span>
                </div>
                <div className="flex-1 text-center md:text-left">
                    <h3 className="text-lg font-bold text-slate-800 mb-1">Extension Required</h3>
                    <p className="text-sm text-slate-500 max-w-xl">
                        Install the Nex Chrome Extension to enable real-time job analysis from any job board.
                    </p>
                </div>
                <button
                    className="whitespace-nowrap bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-xl font-bold text-sm transition-all"
                >
                    Install Extension
                </button>
            </div>
            
        </div>
    );
};

export default CareerLaunchpad;
